import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpRequest, HttpResponse } from '@angular/common/http';

import { OfflineService, StoredQueuedRequest } from './offline.service';

const QUEUEABLE_URL_PATTERNS = [
  '/api/rpc/r_set_work_order_status',
  '/api/rpc/r_set_notification_status',
  '/api/rpc/r_upsert_token'
];

export function isNetworkError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { status?: number; message?: string };
  if (e.status === 0) return true;
  // Gateway timeout from the service worker
  if (e.status === 504) return true;
  if (typeof e.message === 'string' && e.message.toLowerCase().includes('fetch')) return true;
  if (typeof e.message === 'string' && e.message.toLowerCase().includes('network')) return true;
  return false;
}

export function isQueueableRequest(request: HttpRequest<unknown>): boolean {
  const method = request.method?.toUpperCase();
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method ?? '')) return false;
  return QUEUEABLE_URL_PATTERNS.some((pattern) => request.url.includes(pattern));
}

function serializeRequest(request: HttpRequest<unknown>): Omit<StoredQueuedRequest, 'id' | 'createdAt'> {
  return {
    method: request.method,
    url: request.url,
    body: request.body,
  };
}

@Injectable({ providedIn: 'root' })
export class OfflineQueueService {
  private readonly http = inject(HttpClient);
  private readonly offlineService = inject(OfflineService);
  private readonly platformId = inject(PLATFORM_ID);
  private isProcessing = false;

  constructor() {
    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
      window.addEventListener('online', () => this.processQueue());
      if (navigator.onLine) {
        this.processQueue();
      }
    }
  }

  async enqueue(request: HttpRequest<unknown>): Promise<HttpResponse<any>> {
    const id = `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const stored: StoredQueuedRequest = {
      id,
      ...serializeRequest(request),
      createdAt: Date.now(),
    };

    await this.offlineService.addQueuedRequest(stored);
    await this.offlineService.patchCache(request);
    this.processQueue();

    return new HttpResponse({
      body: { 
        message: 'Request queued for offline sync', 
        queueId: id,
        offline: true 
      },
      status: 202, 
      statusText: 'Accepted (Queued)'
    });
  }

  private async processQueue(): Promise<void> {
    if (
      !isPlatformBrowser(this.platformId) ||
      typeof navigator === 'undefined' ||
      !navigator.onLine
    ) {
      return;
    }
    if (this.isProcessing) return;

    const queue = await this.offlineService.getQueuedRequests();
    if (queue.length === 0) return;

    this.isProcessing = true;

    const processNext = async () => {
      const queue = await this.offlineService.getQueuedRequests();
      if (queue.length === 0) {
        this.isProcessing = false;
        return;
      }

      const stored = queue[0];

      this.http
        .request(stored.method, stored.url, { body: stored.body })
        .subscribe({
          next: async () => {
            await this.offlineService.removeFirstQueuedRequest();
            processNext();
          },
          error: async (e) => {
            const removed = await this.offlineService.removeFirstQueuedRequest();
            if (removed && isNetworkError(e)) {
              await this.offlineService.addQueuedRequestAtFront(stored);
            }
            processNext();
          },
        });
    };

    processNext();
  }
}